import { useEffect, useState } from 'react';
import { buildRoute, useApiWorker } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { AddEditSubRoute, ROUTES, useNavigation } from 'Navigation';
import { useHistory } from 'react-router-dom';

export interface ClosedCase {
  id: string;
  claimantName: string;
  stradixNumber: string;
  status: string;
  reportFileName?: string;
}

interface UseReportsReadyForYouHookResult {
  closedCases: ClosedCase[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalRecords: number;
  handlePrev: () => void;
  handleNext: () => void;
  handleFirst: () => void;
  handleLast: () => void;
  handlePageClick: (num: number) => void;
  navigateToReferralDetailPage: (id: string) => void;
  navigateToClosedCasesPage: () => void;
}


export const useReportsReadyForYouHook =
  ({clientId, clientName}: {clientId: string | null, clientName?: string | null}): UseReportsReadyForYouHookResult => {
    const API = useApiWorker();
    const [closedCases, setClosedCases] = useState<ClosedCase[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const totalPages = Math.min(4, Math.ceil(totalRecords / 6));
    const history = useHistory();

    useEffect(() => {
      getClosedCases(page, clientId);
    }, [page, clientId]);

    function navigateToReferralDetailPage(id: string) {
      history.push(buildRoute(AddEditSubRoute.Referral, id, ROUTES.VIEW));
    }
    function navigateToClosedCasesPage() {
      history.push('/Referrals/ClosedReferrals?' + (clientName ? `clientName=${clientName}` : '')+(clientId ? `&clientId=${clientId}` : ''));
    }

    async function getClosedCases(currentPage: number, clientId: string | null) {
      const closedCasesReadEndpoint = ENDPOINTS.closedcases.read;
      const axiosConfig = {
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          'x-api-version': '3.0',
        },
      };

      try {
        setLoading(true);
        const { data } = await API.post(
          `${closedCasesReadEndpoint}?page=${currentPage}&pageSize=6&sort=closedDate-desc`,
          {
            clientId: clientId,
          },
          axiosConfig
        );
        setClosedCases(data.data);
        setTotalRecords(data.total || data.totalCount || 0);
      } catch (error) {
        console.error('Error fetching closed cases:', error);
      } finally {
        setLoading(false);
      }
    }

    const handlePrev = () => setPage((p) => Math.max(1, p - 1));
    const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));
    const handleFirst = () => setPage(1);
    const handleLast = () => setPage(totalPages);
    const handlePageClick = (num: number) => setPage(num);

    return {
      closedCases,
      loading,
      page,
      totalPages,
      totalRecords,
      handlePrev,
      handleNext,
      handleFirst,
      handleLast,
      handlePageClick,
      navigateToClosedCasesPage,
      navigateToReferralDetailPage,
    };
  };
